import React from "react";
import { ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getTranslationsForLanguage } from "@/i18n";

export default function PayPalUserPaymentOption({ planName, planTier = "premium", price, className = "", copy, priceSuffix = "/mo" }) {
  const labels = copy || getTranslationsForLanguage("en", "billing").paypal;
  const [isStarting, setIsStarting] = React.useState(false);
  const [error, setError] = React.useState("");

  const startCheckout = async () => {
    setIsStarting(true);
    setError("");
    const returnUrl = `${window.location.origin}/billing/paypal-return`;
    const cancelUrl = `${window.location.origin}/billing`;

    try {
      const response = await base44.functions.invoke("payPalCreateOrder", {
        plan_name: planName || "Marstick Premium",
        plan_tier: planTier,
        return_url: returnUrl,
        cancel_url: cancelUrl,
      });
      const approvalUrl = response?.data?.approval_url;
      if (!approvalUrl) throw new Error("PayPal did not return an approval link.");
      window.location.href = approvalUrl;
    } catch (checkoutError) {
      setError(checkoutError?.response?.data?.error || checkoutError.message || "Could not start PayPal checkout.");
      setIsStarting(false);
    }
  };

  return (
    <Card className={`border-blue-200/80 bg-gradient-to-br from-blue-50 via-white to-cyan-50/70 shadow-sm ${className}`}>
      <CardContent className="space-y-4 p-4 sm:p-5">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-blue-100">
              <span className="text-[11px] font-black tracking-tight text-[#003087]">Pay</span><span className="text-[11px] font-black tracking-tight text-[#009cde]">Pal</span>
            </div>
            <div className="min-w-0">
              <p className="break-words text-base font-black leading-6 text-slate-950">{labels.title}</p>
              <p className="mt-1 break-words text-sm leading-6 text-slate-600">{labels.description}</p>
            </div>
          </div>
          <Badge variant="outline" className="w-fit shrink-0 border-blue-200 bg-white px-3 py-1 text-blue-700">{labels.recommended}</Badge>
        </div>

        {planName && (
          <div className="rounded-xl border border-blue-100 bg-white/90 p-3 text-sm shadow-sm">
            <div className="grid gap-1 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-3">
              <span className="text-slate-600">{labels.selectedPlan}</span>
              <span className="break-words font-black text-slate-950 sm:text-right">{planName}{price ? ` · $${price}${priceSuffix}` : ""}</span>
            </div>
          </div>
        )}

        <Button onClick={startCheckout} disabled={isStarting} className="min-h-12 w-full bg-[#003087] px-4 text-sm font-black leading-5 text-white hover:bg-[#00246a]">
          {isStarting ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : null}
          <span className="break-words">{isStarting ? labels.openingPaypal || "Opening PayPal…" : labels.continue}</span> <ExternalLink className="h-4 w-4 shrink-0" />
        </Button>

        {error && <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-semibold leading-5 text-red-700">{error}</p>}

        <p className="flex items-start gap-2 text-xs leading-5 text-slate-600">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> <span className="break-words">{labels.secure}</span>
        </p>
      </CardContent>
    </Card>
  );
}